import { msg, Trans } from '@lingui/macro'

import * as React from 'react'

import { useTrans } from '~logic/i18n/useTrans'
import { TProductDetailState } from '~logic/xstate/machines/menu/productDetailMachineTypes'
import { useAddonDetailControlCtx } from '~shared/segments/AddonDetail'
import { useProductDetailStateUpdaters } from '~shared/segments/ProductDetail'
import { Box } from '~ui/components/layout/Box'
import { AddonCategory } from '~ui/components/ware/addons/AddonCategory'

import { useLayoutCtx } from '../../../config/contexts/LayoutProvider'

type TAddonCategory = TProductDetailState['addonCategories'][number]

type TProps = NoChildren &
  SkeletonProps & {
    category: TAddonCategory
  }

/**
 * Addon category in a product detail.
 * Generates category validation messages to inform a customer about minimal and/or maximal addon counts.
 */
export const AddonListCategory: React.FC<TProps> = ({ category, showSkeleton = false }) => {
  const { trans } = useTrans()
  const { isMobile } = useLayoutCtx()

  const { openAddonDetail } = useAddonDetailControlCtx()
  const { updateAddonState } = useProductDetailStateUpdaters()

  return (
    <Box id={category.id} padding={{ horizontal: isMobile ? `medium` : `none`, vertical: `none` }}>
      <AddonCategory
        hasDivider
        moreInfoLabel={trans(msg`more`)}
        showSkeleton={showSkeleton}
        nameLabel={category.nameLabel}
        addonState={category.addons}
        maxTotalCount={category.maxTotalCount}
        minTotalCount={category.minTotalCount}
        cancelSelectionLabel={trans(msg`clear selection`)}
        getValidationMessage={getValidationMessage}
        maxIndividualCount={category.maxIndividualCount}
        onSelect={(addons) => {
          if (showSkeleton) {
            return
          }

          updateAddonState({ category: { ...category, addons } })
        }}
        onAddonDetailClick={(addon) => {
          if (showSkeleton) {
            return
          }

          openAddonDetail(addon)
        }}
      />
    </Box>
  )
}

/**
 * Provides translated message to the addon category. The message informs a customer about minimal and/or maximal addon counts he/she can add.
 */
const getValidationMessage: PropsOf<typeof AddonCategory>['getValidationMessage'] = (meta) => {
  switch (meta.messageType) {
    case `only`:
      return <Trans>Select {meta.count} of the options below</Trans>
    case `min`:
      return <Trans>Select at least {meta.minCount} of the options below</Trans>
    case `max`:
      return <Trans>Select up to {meta.maxCount} of the options below</Trans>
    case `minmax`:
      return (
        <Trans>
          Select at least {meta.minCount} and at most {meta.maxCount} of the options below
        </Trans>
      )
    default:
      return undefined
  }
}
